<template>
  <div class="max-w-7xl mx-auto">
    <!-- Competition Header -->
    <CompetitionHeader
      v-if="competition"
      :competition="competition"
      :participant-count="leaderboard?.entries?.length || 0"
      :show-delete-button="false"
      :loading="loading"
    />

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <span class="loading loading-spinner loading-lg"></span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-error">
      <i class="mdi mdi-alert-circle h-6 w-6"></i>
      <span>{{ error }}</span>
    </div>

    <!-- Competition Not Active -->
    <div v-else-if="competition && !competition.is_locked" class="text-center py-12">
      <div class="max-w-md mx-auto">
        <div class="text-6xl mb-4">⏰</div>
        <h4 class="text-lg font-semibold mb-2">Competition Not Started</h4>
        <p class="text-base-content/70 mb-6">This competition hasn't started yet.</p>
        <router-link :to="`/competition/${competitionId}`" class="btn btn-primary">
          View Competition Details
        </router-link>
      </div>
    </div>

    <!-- Active Competition Content -->
    <div v-else-if="competition && competition.is_locked" class="grid lg:grid-cols-3 gap-6">
      <!-- Live Leaderboard -->
      <div class="lg:col-span-2">
        <div class="card bg-base-100 card-bordered">
          <div class="card-body">
            <div class="flex justify-between items-center mb-4">
              <h3 class="card-title text-xl">Live Leaderboard</h3>
              <div class="text-xs text-base-content/50">
                Auto-refreshes every 30s
              </div>
            </div>
            
            <div v-if="!leaderboard || leaderboard.entries.length === 0" class="text-center py-12">
              <p class="text-base-content/70">No entries found for this competition.</p>
            </div>
            
            <div v-else class="overflow-x-auto">
              <table class="table table-zebra w-full">
                <thead>
                  <tr>
                    <th class="w-16">Rank</th>
                    <th>Entry</th>
                    <th class="w-24">Return</th>
                    <th class="w-32">Value</th>
                    <th class="w-24">Daily Avg</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(entry, index) in leaderboard.entries" :key="entry.entry_id">
                    <td>
                      <div class="flex items-center gap-2">
                        <span v-if="index === 0" class="text-yellow-500 text-lg">🥇</span>
                        <span v-else-if="index === 1" class="text-gray-400 text-lg">🥈</span>
                        <span v-else-if="index === 2" class="text-amber-600 text-lg">🥉</span>
                        <span v-else class="text-base-content/50 font-medium">{{ index + 1 }}</span>
                      </div>
                    </td>
                    <td class="font-semibold">{{ entry.display_name }}</td>
                    <td>
                      <span :class="entry.total_return >= 0 ? 'text-success' : 'text-error'">
                        {{ entry.total_return >= 0 ? '+' : '' }}{{ entry.total_return.toFixed(2) }}%
                      </span>
                    </td>
                    <td class="font-mono">${{ entry.portfolio_value.toFixed(2) }}</td>
                    <td>
                      <span v-if="entry.average_daily_return !== null" :class="entry.average_daily_return >= 0 ? 'text-success' : 'text-error'">
                        {{ entry.average_daily_return >= 0 ? '+' : '' }}{{ entry.average_daily_return.toFixed(2) }}%
                      </span>
                      <span v-else class="text-base-content/50">-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div class="text-xs text-base-content/50 mt-4">
              Last updated: {{ new Date(leaderboard.last_updated).toLocaleString() }}
            </div>
          </div>
        </div>
      </div>

      <!-- Competition Stats -->
      <div class="lg:col-span-1">
        <div class="card bg-base-100 card-bordered sticky top-4">
          <div class="card-body">
            <h3 class="card-title text-lg mb-4">Competition Stats</h3>
            
            <div class="space-y-4">
              <div>
                <p class="text-sm text-base-content/70">Days Remaining</p>
                <p class="font-semibold text-lg">{{ daysRemaining }}</p>
              </div>
              
              <div>
                <p class="text-sm text-base-content/70">Total Participants</p>
                <p class="font-semibold">{{ leaderboard?.entries?.length || 0 }}</p>
              </div>
              
              <div v-if="leaderboard && leaderboard.entries.length > 0">
                <p class="text-sm text-base-content/70">Best Performance</p>
                <p class="font-semibold text-success">
                  +{{ leaderboard.entries[0].total_return.toFixed(2) }}%
                </p>
              </div>
              
              <div v-if="leaderboard && leaderboard.entries.length > 0">
                <p class="text-sm text-base-content/70">Average Return</p>
                <p class="font-semibold">
                  <span :class="averageReturn >= 0 ? 'text-success' : 'text-error'">
                    {{ averageReturn >= 0 ? '+' : '' }}{{ averageReturn.toFixed(2) }}%
                  </span>
                </p>
              </div>
            </div>
            
            <div class="divider"></div>
            
            <div class="text-center">
              <router-link :to="`/competition/${competitionId}`" class="btn btn-outline w-full">
                View Competition Details
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useCompetitionStore } from '../stores/competition'
import CompetitionHeader from '../components/CompetitionHeader.vue'

const route = useRoute()
const competitionStore = useCompetitionStore()

const competitionId = route.params.id
const competition = ref(null)
const leaderboard = ref(null)
const loading = ref(false)
const error = ref('')

const daysRemaining = computed(() => {
  if (!competition.value) return 0
  
  const now = new Date()
  const endDate = new Date(competition.value.start_date)
  endDate.setDate(endDate.getDate() + competition.value.duration_days)
  
  const timeDiff = endDate - now
  const days = Math.ceil(timeDiff / (1000 * 60 * 60 * 24))
  
  return Math.max(0, days)
})

const averageReturn = computed(() => {
  if (!leaderboard.value || leaderboard.value.entries.length === 0) return 0
  
  const totalReturn = leaderboard.value.entries.reduce((sum, entry) => sum + entry.total_return, 0)
  return totalReturn / leaderboard.value.entries.length
})

const fetchData = async () => {
  loading.value = true
  error.value = ''
  
  try {
    // Fetch competition details
    competition.value = await competitionStore.fetchCompetition(competitionId)
    
    // Only fetch leaderboard if competition is active
    if (competition.value && competition.value.is_locked) {
      leaderboard.value = await competitionStore.fetchActiveCompetition(competitionId)
    }
  } catch (err) {
    error.value = err.message || 'Failed to load competition data'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  setTimeout(() => {
    fetchData()
  }, 100)
})

// Refresh data every 30 seconds for active competitions
let refreshInterval = null

watch(() => competition.value?.is_locked, (isLocked) => {
  if (isLocked) {
    refreshInterval = setInterval(fetchData, 30000)
  } else {
    if (refreshInterval) {
      clearInterval(refreshInterval)
      refreshInterval = null
    }
  }
})

// Cleanup intervals on component unmount
onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
})
</script> 